import { Route } from 'vue-router';
import { getDefaultRobots } from '@/utils/publicSite';
import { BlogEntry } from '@/views/blog/models/Blog';
import { toAbsolutePublicUrl } from '@/utils/publicSeo';
import { getLocalPublicBlogEntry } from './localBlogData';

type PublicArticleWithSeoTitle = BlogEntry & {
  seoTitle?: string;
};

export type PublicArticleSeoMeta = {
  canonical: string;
  description: string;
  image: string;
  ogType: 'article';
  robots: string;
  structuredData: Record<string, unknown>;
  title: string;
  twitterCard: 'summary_large_image';
};

export const getPublicArticlePath = (article: Pick<BlogEntry, 'externalId'>) => {
  const articleId = article.externalId || '';
  return `/pblog/articles/${articleId}`;
};

export const getPublicArticleSeoMeta = (article: PublicArticleWithSeoTitle): PublicArticleSeoMeta => {
  const canonical = toAbsolutePublicUrl(getPublicArticlePath(article));
  const image = toAbsolutePublicUrl(article.titleImage || '/BC_preview.png');
  const title = article.seoTitle || article.title;

  return {
    title: `${title} | ByteCookie`,
    description: article.summary,
    robots: getDefaultRobots(),
    canonical,
    image,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.summary,
      image,
      mainEntityOfPage: canonical,
      author: {
        '@type': 'Person',
        name: article.creator?.fullname || 'Marvin (ByteCookie)'
      },
      publisher: {
        '@type': 'Organization',
        name: 'ByteCookie'
      }
    }
  };
};

export const resolvePublicArticleSeoMeta = (route: Pick<Route, 'params'>) => {
  const articleId = String(route.params?.articleId || '');
  const article = getLocalPublicBlogEntry(articleId);

  if (!article) return undefined;

  return {
    article,
    path: getPublicArticlePath(article),
    seoMeta: getPublicArticleSeoMeta(article),
  };
};
